import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@material-ui/core';
import moment from 'moment';
import { formatMoney } from '../../../utils';
import { convertToFinalPrice } from '../../../finalPrice';
import { filterTicketsBySellingRange, isTicketLongExpired } from '../../../utils/ticketHelper';

const ticketSellingRangeText = ticket => {
    const validFrom = ticket.date_from ? moment(ticket.date_from).format('MMM D, YYYY') : '-';
    const validTo = ticket.date_to ? moment(ticket.date_to).format('MMM D, YYYY') : '-';

    if (ticket.date_from && ticket.date_to) {
        return `${validFrom} - ${validTo}`;
    }

    if (ticket.date_from && !ticket.date_to) {
        return `From ${validFrom}`;
    }

    if (!ticket.date_from && ticket.date_to) {
        return `Until ${validTo}`;
    }

    return '';
};

export const TicketSellingRangeChip = ({ ticket }) => {
    const label = ticketSellingRangeText(ticket);

    if (!label) {
        return null;
    }
    return (
        <Chip
            size="small"
            variant="outlined"
            style={{
                marginLeft: 8,
                fontSize: '0.6rem',
                padding: '2px',
                height: '18px'
            }}
            label={label}
        />
    );
};

function TicketBlock({
    activity,
    currentFilters,
    showCommission,
    isAdminOrAgent,
    showTicketSellingRanges = false
}) {
    const checkInDate = currentFilters?.checkInDate;
    const checkOutDate = currentFilters?.checkOutDate;
    const [showLongExpiredTickets, setShowLongExpiredTickets] = useState(false);

    const allTickets = activity?.tickets || [];
    const referenceDate = checkInDate || moment();

    const hasLongExpiredTickets = useMemo(() => {
        return allTickets.some(ticket => isTicketLongExpired(ticket, referenceDate));
    }, [allTickets, referenceDate]);

    const tickets = useMemo(() => {
        let result = allTickets;

        if (showTicketSellingRanges) {
            result = filterTicketsBySellingRange(result, checkInDate, checkOutDate);
        }

        if (!showLongExpiredTickets) {
            result = result.filter(ticket => !isTicketLongExpired(ticket, referenceDate));
        }

        return result;
    }, [
        allTickets,
        checkInDate,
        checkOutDate,
        showTicketSellingRanges,
        showLongExpiredTickets,
        referenceDate
    ]);

    if (tickets.length === 0 || !activity?.firstTimeslot || !isAdminOrAgent) {
        return null;
    }

    return (
        <div style={{ marginLeft: '-16px', marginRight: '-16px' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <span style={{ fontSize: '0.8rem' }}>Name</span>
                        </TableCell>
                        <TableCell align="right">
                            <span style={{ fontSize: '0.8rem' }}>Price</span>
                        </TableCell>
                        {showCommission && (
                            <TableCell align="right">
                                <span style={{ fontSize: '0.8rem' }}>Comm.</span>
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tickets.map(ticket => {
                        const price = convertToFinalPrice(ticket.retail_price, ticket.final_price);

                        return (
                            <TableRow key={ticket.id}>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <span style={{ fontSize: '0.7rem' }}>{ticket.name}</span>
                                        {!showTicketSellingRanges && (
                                            <TicketSellingRangeChip ticket={ticket} />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                        {formatMoney(price)}
                                    </span>
                                </TableCell>
                                {showCommission && (
                                    <TableCell align="right">
                                        <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {formatMoney(ticket.agentCommission)}
                                        </span>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            {!showTicketSellingRanges && hasLongExpiredTickets && (
                <Box
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 8,
                        marginBottom: 4
                    }}
                >
                    <Button
                        size="small"
                        color="primary"
                        style={{
                            minWidth: 'auto',
                            padding: '4px 8px',
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                        onClick={() => setShowLongExpiredTickets(!showLongExpiredTickets)}
                    >
                        {showLongExpiredTickets
                            ? 'Hide tickets expired more than 3 months ago'
                            : 'Show tickets expired more than 3 months ago'}
                    </Button>
                </Box>
            )}
        </div>
    );
}

export default TicketBlock;
